import React, { useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SettingsCard, useSaver } from './SettingsCard';

const CrewEditor = ({ comp, roles, save, onDone }) => {
  const [counts, setCounts] = useState(() => Object.fromEntries(roles.map(r => [r.key, comp.crew.find(c => c.role_key === r.key)?.count || 0])));
  const [busy, run] = useSaver(save);
  const crew = Object.entries(counts).map(([role_key, count]) => ({ role_key, count: Number(count) || 0 }));
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border p-3" data-testid={`crew-editor-${comp.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {roles.map(r => (
          <label key={r.key} className="flex items-center justify-between gap-2 bg-white border rounded-lg px-2.5 py-1.5 text-sm">
            <span className="truncate text-slate-700">{r.label}</span>
            <Input type="number" min={0} max={10} value={counts[r.key]} onChange={e => setCounts(c => ({ ...c, [r.key]: e.target.value }))}
              className="w-14 h-8 text-center" data-testid={`crew-count-${comp.id}-${r.key}`} />
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-3">
        {comp.custom && <Button variant="ghost" size="sm" disabled={busy} onClick={() => run({ crews: { [comp.id]: null } }, 'Back to the sport default').then(ok => ok && onDone())} data-testid={`crew-reset-${comp.id}`}>Use sport default</Button>}
        <Button variant="outline" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" disabled={busy} onClick={() => run({ crews: { [comp.id]: crew } }, 'Crew saved').then(ok => ok && onDone())} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid={`crew-save-${comp.id}`}>
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save crew
        </Button>
      </div>
    </div>
  );
};

export const CrewSettings = ({ data, save }) => {
  const [editing, setEditing] = useState(null);
  const roles = data.settings.roles || [];
  return (
    <SettingsCard title="Officials needed per game" desc="Set the crew for each schedule. Defaults come from the sport." testId="settings-crews">
      <div className="divide-y">
        {data.competitions.map(c => (
          <div key={c.id} className="py-3" data-testid={`crew-row-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {c.sport && <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">{c.sport}</span>}
                  {c.crew.map(x => <span key={x.role_key} className="px-1.5 py-0.5 rounded bg-[#00247D]/10 text-[#00247D] text-[11px] font-semibold">{x.count} × {x.label}</span>)}
                  {!c.crew.length && <span className="text-xs text-slate-400">No officials needed</span>}
                  <span className={`ml-1 text-[10px] font-bold uppercase ${c.custom ? 'text-amber-600' : 'text-slate-400'}`}>{c.custom ? 'Custom' : 'Sport default'}</span>
                </div>
              </div>
              {editing !== c.id && <Button variant="ghost" size="sm" onClick={() => setEditing(c.id)} data-testid={`crew-edit-${c.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
            </div>
            {editing === c.id && <CrewEditor comp={c} roles={roles} save={save} onDone={() => setEditing(null)} />}
          </div>
        ))}
        {!data.competitions.length && <p className="py-4 text-sm text-slate-500">No schedules yet. Create a schedule and its games show up here.</p>}
      </div>
    </SettingsCard>
  );
};
